/**
 * DriveZone — Toast Notification Component
 *
 * Lightweight inline toast system.
 * Triggered by: session invalidation, login errors, general API errors.
 *
 * Usage:
 *   const { showToast } = useToast()
 *   showToast('Your session expired', 'error')
 */

import { useState, useCallback, useEffect } from 'react'
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react'

// ── Types ────────────────────────────────────────────────────────────────────

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  message: string
  type: ToastType
}

// ── Global toast state (module-level singleton) ───────────────────────────────
// Using a simple callback pattern so any component can trigger a toast
// without prop-drilling or React context.

type ToastCallback = (message: string, type: ToastType) => void
let _globalShowToast: ToastCallback | null = null

// eslint-disable-next-line react-refresh/only-export-components -- intentional: this module mixes a global-singleton helper, a hook, and a component by design
export function showGlobalToast(message: string, type: ToastType = 'info') {
  if (_globalShowToast) {
    _globalShowToast(message, type)
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

// eslint-disable-next-line react-refresh/only-export-components -- see note above
export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = Math.random().toString(36).slice(2)
    setToasts((prev) => [...prev, { id, message, type }])
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 5000)
  }, [])

  // Register as global handler when this hook is mounted in App.tsx
  useEffect(() => {
    _globalShowToast = showToast
    return () => {
      _globalShowToast = null
    }
  }, [showToast])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  return { toasts, showToast, dismiss }
}

// ── Component ─────────────────────────────────────────────────────────────────

const icons: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />,
  error:   <AlertCircle  className="w-5 h-5 text-red-500 flex-shrink-0" />,
  warning: <AlertCircle  className="w-5 h-5 text-amber-500 flex-shrink-0" />,
  info:    <Info         className="w-5 h-5 text-primary-500 flex-shrink-0" />,
}

const backgrounds: Record<ToastType, string> = {
  success: 'bg-white border-emerald-200',
  error:   'bg-white border-red-200',
  warning: 'bg-white border-amber-200',
  info:    'bg-white border-primary-200',
}

interface ToastContainerProps {
  toasts: Toast[]
  onDismiss: (id: string) => void
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null

  return (
    <div
      className="fixed top-4 right-4 z-50 flex flex-col gap-2 w-full max-w-sm"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`
            flex items-start gap-3 px-4 py-3 rounded-xl border shadow-modal
            animate-slide-up
            ${backgrounds[toast.type]}
          `}
        >
          {icons[toast.type]}
          <p className="flex-1 text-sm text-slate-700 leading-relaxed">
            {toast.message}
          </p>
          <button
            onClick={() => onDismiss(toast.id)}
            className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Dismiss notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  )
}
